import { ReactNode } from 'react';
import { Avatar, Checkbox, MenuItem, Radio, Stack, Tooltip, Typography } from '@mui/material';
import { AnalyticsTriggerElementProps } from '@common/types';

export const ListFilterSelectItemType = {
  checkbox: 'checkbox',
  radio: 'radio',
  radioHidden: 'radio-hidden',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type ListFilterSelectItemType = (typeof ListFilterSelectItemType)[keyof typeof ListFilterSelectItemType];

const MIN_CHAR_TO_SHOW_TOOLTIP = 43;
export interface ListFilterSelectItemProps {
  label: string;
  icon?: ReactNode;
  endIcon?: ReactNode;
  onClick: () => void;
  selected?: boolean;
  disabled?: boolean;
  type?: ListFilterSelectItemType;
}

export function isListFilterSelectItemProps(props: ListFilterSelectItemProps): props is ListFilterSelectItemProps {
  return props.type === 'checkbox' || props.type === 'radio' || props.type === 'radio-hidden';
}

export default function ListFilterSelectItem({
  label,
  icon,
  endIcon,
  onClick,
  selected = false,
  disabled = false,
  type = 'checkbox',
  ...analyticsProps
}: ListFilterSelectItemProps & AnalyticsTriggerElementProps) {
  const RadioOrCheck = type === 'checkbox' ? Checkbox : Radio;
  const iconElem = icon ? typeof icon === 'string' ? <Avatar src={icon} className="size-20" /> : icon : undefined;
  const endIconElem = endIcon ? (
    typeof endIcon === 'string' ? (
      <Avatar src={endIcon} className="size-20" />
    ) : (
      endIcon
    )
  ) : undefined;

  return (
    <MenuItem
      onClick={onClick}
      disabled={disabled}
      component="div"
      data-testid="list-filter-select-item"
      {...analyticsProps}
    >
      <Stack direction="row" justifyContent="space-between" spacing={1} width="100%">
        <Stack direction="row" alignItems="center" spacing={1} width="100%" data-testid="list-filter-select-item-name">
          {type !== ListFilterSelectItemType.radioHidden && (
            <RadioOrCheck checked={selected} size="small" sx={{ padding: 0 }} />
          )}
          {iconElem}
          <Tooltip title={label.length > MIN_CHAR_TO_SHOW_TOOLTIP && label} placement="top" arrow>
            <Typography variant="inherit" noWrap>
              {label}
            </Typography>
          </Tooltip>
        </Stack>
        {endIconElem}
      </Stack>
    </MenuItem>
  );
}
